import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { getToken, getUserRole, clearAuth } from './utils/auth';
import { LanguageProvider } from './i18n/LanguageContext';

// Auth pages
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyPage from './pages/VerifyPage';
import ResetPassword from './pages/ResetPassword';

// Student pages
import StudentDashboard from './pages/student/StudentDashboard';
import LessonPage from './pages/student/LessonPage';
import PlacementTest from './pages/student/PlacementTest';
import VocabularyBank from './pages/student/VocabularyBank';
import Certificates from './pages/student/Certificates';
import LevelExam from './pages/student/LevelExam';
import BookingPage from './pages/student/BookingPage';
import SubscribePage from './pages/student/SubscribePage';
import SchoolIDCard from './pages/student/SchoolIDCard';
import AdminRegistrations from './pages/admin/AdminRegistrations';
import AdminWebsite from './pages/admin/AdminWebsite';
import VerifyIdPage from './pages/VerifyIdPage';
import VerifyTutorPage from './pages/VerifyTutorPage';
import AdminCheckin from './pages/admin/AdminCheckin';
import AdminAttendance from './pages/admin/AdminAttendance';
import AdminAssessment from './pages/admin/AdminAssessment';
import AssessmentPage from './pages/student/AssessmentPage';
import SchoolHomepage from './pages/SchoolHomepage';
import BookAssessment from './pages/BookAssessment';
import HomeworkPage from './pages/student/HomeworkPage';
import HomeworkDo from './pages/student/HomeworkDo';
import StudentCourse from './pages/student/StudentCourse';
import StudentSessions from './pages/student/StudentSessions';

// Super Admin pages
import SuperAdminOverview from './pages/superadmin/SuperAdminOverview';
import SuperAdminTenants from './pages/superadmin/SuperAdminTenants';
import SuperAdminStudents from './pages/superadmin/SuperAdminStudents';
import SuperAdminRevenue from './pages/superadmin/SuperAdminRevenue';
import SuperAdminAPIUsage from './pages/superadmin/SuperAdminAPIUsage';
import SuperAdminAPICosts from './pages/superadmin/SuperAdminAPICosts';
import EmailLogs from './pages/superadmin/EmailLogs';
import SuperAdminWebsites from './pages/superadmin/SuperAdminWebsites';
import SuperAdminSecurity from './pages/superadmin/SuperAdminSecurity';
import SuperAdminSettings from './pages/superadmin/SuperAdminSettings';

// Tenant Admin pages
import AdminHome from './pages/admin/AdminHome';
import AdminTutors from './pages/admin/AdminTutors';
import AdminStudents from './pages/admin/AdminStudents';
import AdminPayments from './pages/admin/AdminPayments';
import AdminPayouts from './pages/admin/AdminPayouts';
import AdminBilling from './pages/admin/AdminBilling';
import SuperAdminSubscriptions from './pages/superadmin/SuperAdminSubscriptions';
import SuperAdminBankSettings from './pages/superadmin/SuperAdminBankSettings';
import SuperAdminCertificates from './pages/superadmin/SuperAdminCertificates';
import SuperAdminLessonBank from './pages/superadmin/SuperAdminLessonBank';
import AdminBranding from './pages/admin/AdminBranding';
import AdminCertificates from './pages/admin/AdminCertificates';
import AdminBookings from './pages/admin/AdminBookings';
import AdminGroups from './pages/admin/AdminGroups';
import AdminLessonBank from './pages/admin/AdminLessonBank';
import AdminMockTests from './pages/admin/AdminMockTests';
import SuperAdminMockTests from './pages/superadmin/SuperAdminMockTests';
import MockTestPage from './pages/student/MockTestPage';
import MockTestGuest from './pages/MockTestGuest';
import MyMockTests from './pages/student/MyMockTests';
import StudentPayments from './pages/student/StudentPayments';
import MyAssessment from './pages/student/MyAssessment';
import AdminSupplyDemand from './pages/admin/AdminSupplyDemand';
import AdminMarketing from './pages/admin/AdminMarketing';
import AdminSettings from './pages/admin/AdminSettings';
import AdminPricing from './pages/admin/AdminPricing';
import AdminPromotions from './pages/admin/AdminPromotions';
import AdminTutorRates from './pages/admin/AdminTutorRates';

// Tutor pages
import TutorHome from './pages/tutor/TutorHome';
import TutorEarnings from './pages/tutor/TutorEarnings';
import TutorAvailability from './pages/tutor/TutorAvailability';
import TutorIDCard from './pages/tutor/TutorIDCard';
import TutorSchedule from './pages/tutor/TutorSchedule';
import TutorStudents from './pages/tutor/TutorStudents';
import TutorMockTests from './pages/tutor/TutorMockTests';
import TutorProfile from './pages/tutor/TutorProfile';
import TutorPaymentSettings from './pages/tutor/TutorPaymentSettings';
import LessonPresent from './pages/tutor/LessonPresent';
import TutorRecruitment from './pages/TutorRecruitment';

const PrivateRoute = ({ children, allowedRoles }) => {
  const token = getToken();
  const role = getUserRole();
  const isAdminDomain = window.location.hostname === 'admin.speakiq.uk';
  const isAdminPath = window.location.pathname.startsWith('/admin');
  if (!token) return <Navigate to={isAdminPath ? '/admin/login' : '/login'} replace />;
  if (isAdminDomain && role !== 'superadmin') { clearAuth(); return <Navigate to="/login" replace />; }
  if (allowedRoles && !allowedRoles.includes(role)) {
    const homes = { superadmin: '/superadmin', tenant_admin: '/admin', tutor: '/tutor', student: '/dashboard' };
    return <Navigate to={homes[role] || '/login'} replace />;
  }
  return children;
};

const wrap = (roles, Component) => (
  <PrivateRoute allowedRoles={roles}><Component /></PrivateRoute>
);

function App() {
  return (
    <LanguageProvider>
    <Router>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login />} />
        <Route path="/tutor/login" element={<TutorLoginRedirect />} />
        <Route path="/admin/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/book-assessment" element={<BookAssessment />} />
        <Route path="/verify/:code" element={<VerifyPage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/tutor-signup" element={<TutorRecruitment />} />

        {/* Student */}
        <Route path="/dashboard" element={wrap(['student'], StudentDashboard)} />
        <Route path="/mock-test/:sessionId" element={wrap(['student'], MockTestPage)} />
        <Route path="/mock/:token" element={<MockTestGuest />} />
        <Route path="/my-mock-tests" element={wrap(['student'], MyMockTests)} />
        <Route path="/my-assessment" element={wrap(['student'], MyAssessment)} />
        <Route path="/lesson/:type" element={wrap(['student'], LessonPage)} />
        <Route path="/placement" element={<Navigate to="/assessment" replace />} />
        <Route path="/vocabulary" element={wrap(['student'], VocabularyBank)} />
        <Route path="/my-certificates" element={wrap(['student'], Certificates)} />
        <Route path="/level-exam" element={wrap(['student'], LevelExam)} />
        <Route path="/book-tutor" element={wrap(['student'], BookingPage)} />
        <Route path="/subscribe" element={wrap(['student'], SubscribePage)} />
        <Route path="/programmes" element={wrap(['student'], SubscribePage)} />
        <Route path="/my-course" element={wrap(['student'], StudentCourse)} />
        <Route path="/my-payments" element={wrap(['student'], StudentPayments)} />
        <Route path="/my-sessions" element={wrap(['student'], StudentSessions)} />
        <Route path="/homework" element={wrap(['student'], HomeworkPage)} />
        <Route path="/homework/:hwId" element={wrap(['student'], HomeworkDo)} />
        <Route path="/my-id" element={wrap(['student'], SchoolIDCard)} />

        {/* Super Admin - every route has its own unique component */}
        <Route path="/superadmin" element={wrap(['superadmin'], SuperAdminOverview)} />
        <Route path="/superadmin/tenants" element={wrap(['superadmin'], SuperAdminTenants)} />
        <Route path="/superadmin/students" element={wrap(['superadmin'], SuperAdminStudents)} />
        <Route path="/superadmin/revenue" element={wrap(['superadmin'], SuperAdminRevenue)} />
        <Route path="/superadmin/api-usage" element={wrap(['superadmin'], SuperAdminAPIUsage)} />
        <Route path="/superadmin/api-costs" element={wrap(['superadmin'], SuperAdminAPICosts)} />
        <Route path="/superadmin/email-logs" element={wrap(['superadmin'], EmailLogs)} />
        <Route path="/superadmin/websites" element={wrap(['superadmin'], SuperAdminWebsites)} />
        <Route path="/superadmin/security" element={wrap(['superadmin'], SuperAdminSecurity)} />
        <Route path="/superadmin/settings" element={wrap(['superadmin'], SuperAdminSettings)} />
        <Route path="/superadmin/subscriptions" element={wrap(['superadmin'], SuperAdminSubscriptions)} />
        <Route path="/superadmin/bank-settings" element={wrap(['superadmin'], SuperAdminBankSettings)} />
        <Route path="/superadmin/certificates" element={wrap(['superadmin'], SuperAdminCertificates)} />
        <Route path="/superadmin/lesson-bank" element={wrap(['superadmin'], SuperAdminLessonBank)} />
        <Route path="/superadmin/mock-tests" element={wrap(['superadmin'], SuperAdminMockTests)} />

        {/* Partner Admin - every route has its own unique component */}
        <Route path="/admin" element={wrap(['tenant_admin'], AdminHome)} />
        <Route path="/admin/students" element={wrap(['tenant_admin'], AdminStudents)} />
        <Route path="/admin/payments" element={wrap(['tenant_admin'], AdminPayments)} />
        <Route path="/admin/branding" element={wrap(['tenant_admin'], AdminBranding)} />
        <Route path="/admin/bookings" element={wrap(['tenant_admin'], AdminBookings)} />
        <Route path="/admin/groups" element={wrap(['tenant_admin'], AdminGroups)} />
        <Route path="/admin/lesson-bank" element={wrap(['tenant_admin'], AdminLessonBank)} />
        <Route path="/admin/supply-demand" element={wrap(['tenant_admin'], AdminSupplyDemand)} />
        <Route path="/admin/marketing" element={wrap(['tenant_admin'], AdminMarketing)} />
        <Route path="/admin/pricing" element={wrap(['tenant_admin'], AdminPricing)} />
        <Route path="/admin/promotions" element={wrap(['tenant_admin'], AdminPromotions)} />
        <Route path="/admin/tutor-rates" element={wrap(['tenant_admin'], AdminTutorRates)} />
        <Route path="/admin/settings" element={wrap(['tenant_admin'], AdminSettings)} />
        <Route path="/admin/certificates" element={wrap(['tenant_admin'], AdminCertificates)} />
        <Route path="/admin/registrations" element={wrap(['tenant_admin'], AdminRegistrations)} />
        <Route path="/admin/tutors" element={wrap(['tenant_admin'], AdminTutors)} />
        <Route path="/admin/website" element={wrap(['tenant_admin'], AdminWebsite)} />
        <Route path="/admin/payouts" element={wrap(['tenant_admin'], AdminPayouts)} />
        <Route path="/admin/billing" element={wrap(['tenant_admin'], AdminBilling)} />

        {/* Tutor */}
        <Route path="/tutor" element={wrap(['tutor'], TutorHome)} />
        <Route path="/tutor/earnings" element={wrap(['tutor'], TutorEarnings)} />
        <Route path="/tutor/availability" element={wrap(['tutor'], TutorAvailability)} />
        <Route path="/tutor/my-id" element={wrap(['tutor'], TutorIDCard)} />
        <Route path="/tutor/schedule" element={wrap(['tutor'], TutorSchedule)} />
        <Route path="/tutor/students" element={wrap(['tutor'], TutorStudents)} />
        <Route path="/tutor/mock-tests" element={wrap(['tutor'], TutorMockTests)} />
        <Route path="/tutor/profile" element={wrap(['tutor'], TutorProfile)} />
        <Route path="/tutor/payment-settings" element={wrap(['tutor'], TutorPaymentSettings)} />
        <Route path="/tutor/lesson/:materialId/present" element={wrap(['tutor', 'tenant_admin'], LessonPresent)} />

        {/* Public utility */}
        <Route path="/verify-id/:idNumber" element={<VerifyIdPage />} />
        <Route path="/verify-tutor/:tutorIdNumber" element={<VerifyTutorPage />} />
        <Route path="/admin/checkin" element={<AdminCheckin />} />
        <Route path="/admin/attendance" element={<AdminAttendance />} />
        <Route path="/admin/assessment" element={<AdminAssessment />} />
        <Route path="/admin/mock-tests" element={wrap(['tenant_admin'], AdminMockTests)} />
        <Route path="/assessment" element={<AssessmentPage />} />
        <Route path="/school/:subdomain" element={<SchoolHomepage />} />
        <Route path="/" element={<HomeRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
    </LanguageProvider>
  );
}

function getTenantSubdomain() {
  const h = window.location.hostname;
  // Match pattern like talk2talk.speakiq.uk but NOT admin/tutors/www
  const m = h.match(/^([a-z0-9-]+)\.speakiq\.uk$/);
  if (!m) return null;
  return ['admin', 'tutors', 'www', 'app'].includes(m[1]) ? null : m[1];
}

// Tutors log in on the tutors subdomain — send any /tutor/login hit there.
function TutorLoginRedirect() {
  useEffect(() => { window.location.replace('https://tutors.speakiq.uk/login'); }, []);
  return null;
}

function HomeRedirect() {
  const tenantSub = getTenantSubdomain();
  const role = getUserRole();
  const token = getToken();
  const isAdminDomain = window.location.hostname === 'admin.speakiq.uk';

  // If on admin.speakiq.uk but logged in as wrong role, clear and force re-login
  if (isAdminDomain && token && role !== 'superadmin') {
    clearAuth();
    return <Navigate to="/login" replace />;
  }

  // On a tenant subdomain -> always show the public school homepage at root "/"
  // Logged-in users can still navigate to /dashboard, /admin etc directly
  if (tenantSub) return <SchoolHomepage forceSubdomain={tenantSub} />;
  if (!token) return <Navigate to="/login" replace />;
  if (role === 'student') return <Navigate to="/dashboard" replace />;
  if (role === 'tenant_admin') return <Navigate to="/admin" replace />;
  if (role === 'superadmin') return <Navigate to="/superadmin" replace />;
  if (role === 'tutor') return <Navigate to="/tutor" replace />;
  return <Navigate to="/login" replace />;
}

export default App;
